import { z } from "zod";
import type { Operation } from "../types";

/**
 * RO-2-02 — the Integrations hub server actions
 * (`excellent/app/settings/integrations/_actions.ts`), given a contracted
 * HTTP twin so the Hono host can serve them (Next server actions have no
 * Hono equivalent — see docs/rollover/AGENT-BRIEF.md).
 *
 * Every op here is body-only (no path params) — the source actions already
 * take their ids as plain function args, so a body field is the direct,
 * no-rewrite translation; this also sidesteps Hono's registration-order
 * hazard for `:param` routes entirely (#44370). `reconnectAction` is the one
 * QUERY-classified action in this file (docs/rollover/inventory/actions.md
 * line 534) — no DB write in its body — so it is a GET with query params,
 * matching the inventory's rule (revalidatePath / a write-verb call), not a
 * live security read of the handler's external side effect (it re-runs
 * `transport.beginOAuth`, which mints server-side OAuth state — flagged in
 * the RO-2-02 task report, not silently reclassified).
 *
 * Secrets (client_secret, api key) travel in the body over HTTPS/loopback
 * exactly as the original action received them — never logged, never echoed
 * back (mirrors the action docstrings + CLAUDE.md's integrations rule).
 */

const connectionAttribution = z
  .object({
    projectId: z.number().int().nullable().optional(),
    spansProperties: z.boolean().optional(),
  })
  .optional();

// ---------- seed ----------

const applySeedResponse = z.object({ ok: z.boolean(), providers: z.number() });
export type ApplySeedResponse = z.infer<typeof applySeedResponse>;
const applySeedOp: Operation<void, void, void, ApplySeedResponse> = {
  method: "POST",
  path: () => "/api/settings/integrations/seed",
  response: applySeedResponse,
};

// ---------- OAuth app (operator-supplied client id/secret) ----------

const saveOAuthAppBody = z.object({
  slug: z.string().min(1),
  clientId: z.string(),
  clientSecret: z.string(),
});
type SaveOAuthAppBody = z.infer<typeof saveOAuthAppBody>;
const saveOAuthAppResponse = z.object({ ok: z.boolean(), reason: z.string().optional() });
export type SaveOAuthAppResponse = z.infer<typeof saveOAuthAppResponse>;
const saveOAuthAppOp: Operation<void, void, SaveOAuthAppBody, SaveOAuthAppResponse> = {
  method: "POST",
  path: () => "/api/settings/integrations/oauth-app",
  body: saveOAuthAppBody,
  response: saveOAuthAppResponse,
};

// ---------- begin OAuth ----------

const beginOAuthBody = z.object({
  slug: z.string().min(1),
  attribution: connectionAttribution,
  account: z.string().optional(),
});
type BeginOAuthBody = z.infer<typeof beginOAuthBody>;
const beginOAuthResponse = z.object({
  ok: z.boolean(),
  authorizeUrl: z.string().optional(),
  reason: z.string().optional(),
  message: z.string().optional(),
});
export type BeginOAuthResponse = z.infer<typeof beginOAuthResponse>;
const beginOAuthOp: Operation<void, void, BeginOAuthBody, BeginOAuthResponse> = {
  method: "POST",
  path: () => "/api/settings/integrations/oauth/begin",
  body: beginOAuthBody,
  response: beginOAuthResponse,
};

// ---------- reconnect (MUTATION — it writes; see below) ----------

const reconnectQuery = z.object({ connectionId: z.string() });
type ReconnectQuery = z.infer<typeof reconnectQuery>;
const reconnectResponse = z.object({
  ok: z.boolean(),
  authorizeUrl: z.string().optional(),
  reason: z.string().optional(),
  message: z.string().optional(),
});
export type ReconnectResponse = z.infer<typeof reconnectResponse>;
// POST, not GET, and the method is load-bearing: `authorizeRequest` waives
// GET/HEAD *before* consulting any descriptor (`authz.ts` READ_METHODS), so a
// writing GET is ungated by construction. This route writes — beginOAuth ->
// saveOAuthState -> settingSet — and was ported as a GET because the RO-2-01
// inventory classified it QUERY on "no write-verb call in its own body", which is
// true of the body and false transitively. A viewer holding no mutate capability
// got 200 and wrote a row, while the POST twin on the identical primitive got 403
// (#44285 reject). Mounted `connection:mutate`/`connection`/PRIVILEGED — the same
// descriptor as the `oauth/begin` twin, flag included: without `privileged` the
// descriptor also admits a plain member via the `*:mutate:own` fallback, which the
// route perimeter has no Tier-2 ownership check to resolve (#44285 reject 2).
const reconnectOp: Operation<void, ReconnectQuery, void, ReconnectResponse> = {
  method: "POST",
  path: () => "/api/settings/integrations/oauth/reconnect",
  query: reconnectQuery,
  response: reconnectResponse,
};

// ---------- API key connect ----------

const setApiKeyBody = z.object({
  slug: z.string().min(1),
  key: z.string(),
  label: z.string().optional(),
  attribution: connectionAttribution,
  account: z.string().optional(),
});
type SetApiKeyBody = z.infer<typeof setApiKeyBody>;
const setApiKeyResponse = z.object({
  ok: z.boolean(),
  reason: z.string().optional(),
  message: z.string().optional(),
  connectionId: z.number().optional(),
});
export type SetApiKeyResponse = z.infer<typeof setApiKeyResponse>;
const setApiKeyOp: Operation<void, void, SetApiKeyBody, SetApiKeyResponse> = {
  method: "POST",
  path: () => "/api/settings/integrations/api-key",
  body: setApiKeyBody,
  response: setApiKeyResponse,
};

// ---------- sync configure ----------

const configureSyncBody = z.object({
  connectionId: z.number().int(),
  product: z.string(),
  model: z.string(),
  direction: z.enum(["pull", "push", "bidi"]).optional(),
});
type ConfigureSyncBody = z.infer<typeof configureSyncBody>;
const configureSyncResponse = z.object({ ok: z.boolean(), syncId: z.number() });
export type ConfigureSyncResponse = z.infer<typeof configureSyncResponse>;
const configureSyncOp: Operation<void, void, ConfigureSyncBody, ConfigureSyncResponse> = {
  method: "POST",
  path: () => "/api/settings/integrations/syncs",
  body: configureSyncBody,
  response: configureSyncResponse,
};

// ---------- sync schedule ----------

const setSyncScheduleBody = z.object({
  syncId: z.number().int(),
  connectionId: z.number().int(),
  schedule: z.enum(["manual", "interval"]),
  frequencySec: z.number().int().optional(),
  enabled: z.boolean().optional(),
});
type SetSyncScheduleBody = z.infer<typeof setSyncScheduleBody>;
const setSyncScheduleResponse = z.object({ ok: z.boolean() });
export type SetSyncScheduleResponse = z.infer<typeof setSyncScheduleResponse>;
const setSyncScheduleOp: Operation<void, void, SetSyncScheduleBody, SetSyncScheduleResponse> = {
  method: "POST",
  path: () => "/api/settings/integrations/syncs/schedule",
  body: setSyncScheduleBody,
  response: setSyncScheduleResponse,
};

// ---------- sync now (one sync) ----------

const syncNowBody = z.object({ syncId: z.number().int(), connectionId: z.number().int() });
type SyncNowBody = z.infer<typeof syncNowBody>;
const syncNowResponse = z.object({
  ok: z.boolean(),
  mapped: z.number().optional(),
  failed: z.number().optional(),
  error: z.string().optional(),
});
export type SyncNowResponse = z.infer<typeof syncNowResponse>;
const syncNowOp: Operation<void, void, SyncNowBody, SyncNowResponse> = {
  method: "POST",
  path: () => "/api/settings/integrations/syncs/run",
  body: syncNowBody,
  response: syncNowResponse,
};

// ---------- sync all (every pull sync on a connection) ----------

const syncAllBody = z.object({ connectionId: z.number().int() });
type SyncAllBody = z.infer<typeof syncAllBody>;
const syncAllResponse = z.object({
  ok: z.boolean(),
  syncs: z.number(),
  mapped: z.number(),
  failed: z.number(),
});
export type SyncAllResponse = z.infer<typeof syncAllResponse>;
const syncAllOp: Operation<void, void, SyncAllBody, SyncAllResponse> = {
  method: "POST",
  path: () => "/api/settings/integrations/connections/sync-all",
  body: syncAllBody,
  response: syncAllResponse,
};

// ---------- connection-level automatic-sync toggle ----------

const setConnectionAutoSyncBody = z.object({
  connectionId: z.number().int(),
  enabled: z.boolean(),
});
type SetConnectionAutoSyncBody = z.infer<typeof setConnectionAutoSyncBody>;
const setConnectionAutoSyncResponse = z.object({ ok: z.boolean(), syncs: z.number() });
export type SetConnectionAutoSyncResponse = z.infer<typeof setConnectionAutoSyncResponse>;
const setConnectionAutoSyncOp: Operation<
  void,
  void,
  SetConnectionAutoSyncBody,
  SetConnectionAutoSyncResponse
> = {
  method: "POST",
  path: () => "/api/settings/integrations/connections/auto-sync",
  body: setConnectionAutoSyncBody,
  response: setConnectionAutoSyncResponse,
};

// ---------- flush outbox ----------

const flushOutboxBody = z.object({ connectionId: z.number().int() });
type FlushOutboxBody = z.infer<typeof flushOutboxBody>;
const flushOutboxResponse = z.object({
  ok: z.boolean(),
  sent: z.number().optional(),
  failed: z.number().optional(),
  skipped: z.number().optional(),
});
export type FlushOutboxResponse = z.infer<typeof flushOutboxResponse>;
const flushOutboxOp: Operation<void, void, FlushOutboxBody, FlushOutboxResponse> = {
  method: "POST",
  path: () => "/api/settings/integrations/connections/flush-outbox",
  body: flushOutboxBody,
  response: flushOutboxResponse,
};

// ---------- retry one outbox item ----------

const retryOutboxItemBody = z.object({
  outboxId: z.number().int(),
  connectionId: z.number().int(),
});
type RetryOutboxItemBody = z.infer<typeof retryOutboxItemBody>;
const retryOutboxItemResponse = z.object({
  ok: z.boolean(),
  reason: z.string().optional(),
  sent: z.number().optional(),
  failed: z.number().optional(),
});
export type RetryOutboxItemResponse = z.infer<typeof retryOutboxItemResponse>;
const retryOutboxItemOp: Operation<void, void, RetryOutboxItemBody, RetryOutboxItemResponse> = {
  method: "POST",
  path: () => "/api/settings/integrations/outbox/retry",
  body: retryOutboxItemBody,
  response: retryOutboxItemResponse,
};

// ---------- push one entity ----------

const pushBody = z.object({ urn: z.string().min(1) });
type PushBody = z.infer<typeof pushBody>;
const pushResponse = z.object({
  ok: z.boolean(),
  reason: z.string().optional(),
  sent: z.number().optional(),
});
export type PushResponse = z.infer<typeof pushResponse>;
const pushOp: Operation<void, void, PushBody, PushResponse> = {
  method: "POST",
  path: () => "/api/settings/integrations/push",
  body: pushBody,
  response: pushResponse,
};

// ---------- save per-connection config ----------

const saveConnectionConfigBody = z.object({
  connectionId: z.number().int(),
  config: z.record(z.string()),
});
type SaveConnectionConfigBody = z.infer<typeof saveConnectionConfigBody>;
const saveConnectionConfigResponse = z.object({ ok: z.boolean(), reason: z.string().optional() });
export type SaveConnectionConfigResponse = z.infer<typeof saveConnectionConfigResponse>;
const saveConnectionConfigOp: Operation<
  void,
  void,
  SaveConnectionConfigBody,
  SaveConnectionConfigResponse
> = {
  method: "POST",
  path: () => "/api/settings/integrations/connections/config",
  body: saveConnectionConfigBody,
  response: saveConnectionConfigResponse,
};

// ---------- poller enable/disable ----------

const setPollerEnabledBody = z.object({ enabled: z.boolean() });
type SetPollerEnabledBody = z.infer<typeof setPollerEnabledBody>;
const setPollerEnabledResponse = z.object({ ok: z.boolean() });
export type SetPollerEnabledResponse = z.infer<typeof setPollerEnabledResponse>;
const setPollerEnabledOp: Operation<void, void, SetPollerEnabledBody, SetPollerEnabledResponse> = {
  method: "POST",
  path: () => "/api/settings/integrations/poller",
  body: setPollerEnabledBody,
  response: setPollerEnabledResponse,
};

// ---------- run every due interval sync now ----------

const runDueNowResponse = z.object({ ok: z.boolean(), ran: z.number() });
export type RunDueNowResponse = z.infer<typeof runDueNowResponse>;
const runDueNowOp: Operation<void, void, void, RunDueNowResponse> = {
  method: "POST",
  path: () => "/api/settings/integrations/run-due",
  response: runDueNowResponse,
};

// ---------- re-run account-info enrichment ----------

const enrichFromConnectionBody = z.object({ connectionId: z.number().int() });
type EnrichFromConnectionBody = z.infer<typeof enrichFromConnectionBody>;
const enrichFromConnectionResponse = z.object({
  ok: z.boolean(),
  reason: z.string().optional(),
  fields_written: z.number().optional(),
  fields_skipped: z.number().optional(),
});
export type EnrichFromConnectionResponse = z.infer<typeof enrichFromConnectionResponse>;
const enrichFromConnectionOp: Operation<
  void,
  void,
  EnrichFromConnectionBody,
  EnrichFromConnectionResponse
> = {
  method: "POST",
  path: () => "/api/settings/integrations/connections/enrich",
  body: enrichFromConnectionBody,
  response: enrichFromConnectionResponse,
};

// ---------- disconnect (archive) a connection ----------

const disconnectBody = z.object({ connectionId: z.number().int() });
type DisconnectBody = z.infer<typeof disconnectBody>;
const disconnectResponse = z.object({ ok: z.boolean() });
export type DisconnectResponse = z.infer<typeof disconnectResponse>;
const disconnectOp: Operation<void, void, DisconnectBody, DisconnectResponse> = {
  method: "POST",
  path: () => "/api/settings/integrations/connections/disconnect",
  body: disconnectBody,
  response: disconnectResponse,
};

export const settingsIntegrations = {
  applySeed: applySeedOp,
  saveOAuthApp: saveOAuthAppOp,
  beginOAuth: beginOAuthOp,
  reconnect: reconnectOp,
  setApiKey: setApiKeyOp,
  configureSync: configureSyncOp,
  setSyncSchedule: setSyncScheduleOp,
  syncNow: syncNowOp,
  syncAll: syncAllOp,
  setConnectionAutoSync: setConnectionAutoSyncOp,
  flushOutbox: flushOutboxOp,
  retryOutboxItem: retryOutboxItemOp,
  push: pushOp,
  saveConnectionConfig: saveConnectionConfigOp,
  setPollerEnabled: setPollerEnabledOp,
  runDueNow: runDueNowOp,
  enrichFromConnection: enrichFromConnectionOp,
  disconnect: disconnectOp,
};
